import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { tolerantId } from "../../utils/firestoreHelpers";
import { cargarPersonalEdificio, buscarColaborador } from "../../utils/personalEdificio";
import {
  FaUserClock,
  FaUsers,
  FaClock,
  FaFilter,
  FaSearch,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import LoadingState from "../../components/LoadingState";
import { colors, radius, spacing, typography, cardBase, pageContainer } from "../../utils/designTokens";

const AsistenciaEmpleados = () => {
  const { selectedBuilding } = useBuilding();
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [todayStats, setTodayStats] = useState({ present: 0, avgHours: 0 });

  const buildingId = selectedBuilding?.buildingid;

  useEffect(() => {
    if (!buildingId) return;
    fetchEmployees();
  }, [buildingId]);

  useEffect(() => {
    if (!buildingId) return;
    fetchAttendance();
  }, [buildingId, selectedEmployee, dateFrom, dateTo]);

  // Personal del edificio: employeebuilding por `building` + nombre de la
  // ficha users (utils/personalEdificio). Sin escanear la colección employee.
  const fetchEmployees = async () => {
    try {
      setEmployees(await cargarPersonalEdificio(buildingId));
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    }
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      let q = query(
        collection(db, "attendance"),
        where("buildingid", "in", tolerantId(buildingId)),
        orderBy("date", "desc")
      );

      const snap = await getDocs(q);
      let records = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (selectedEmployee) {
        records = records.filter(
          (r) => String(r.employeeid) === String(selectedEmployee)
        );
      }

      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        records = records.filter((r) => {
          const d = r.date?.toDate ? r.date.toDate() : new Date(r.date);
          return d >= from;
        });
      }

      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        records = records.filter((r) => {
          const d = r.date?.toDate ? r.date.toDate() : new Date(r.date);
          return d <= to;
        });
      }

      // El nombre se resuelve al mostrar (columna Empleado): el personal puede
      // terminar de cargar después que la asistencia.
      setAttendance(records);
      computeTodayStats(records);
    } catch (err) {
      console.error("Error fetching attendance:", err);
    }
    setLoading(false);
  };

  const computeTodayStats = (records) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayRecords = records.filter((r) => {
      const d = r.date?.toDate ? r.date.toDate() : new Date(r.date);
      return d >= today && d < tomorrow;
    });

    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekRecords = records.filter((r) => {
      const d = r.date?.toDate ? r.date.toDate() : new Date(r.date);
      return d >= weekAgo && d < tomorrow;
    });

    const totalHoursWeek = weekRecords.reduce(
      (sum, r) => sum + (r.hoursWorked || 0),
      0
    );
    const avgHours =
      weekRecords.length > 0
        ? (totalHoursWeek / weekRecords.length).toFixed(1)
        : 0;

    setTodayStats({
      present: todayRecords.length,
      avgHours,
    });
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (val) => {
    if (!val) return "-";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleTimeString("es-UY", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatus = (record) => {
    if (record.status === "ausente" || (!record.checkIn && !record.checkOut)) {
      return "ausente";
    }
    return "presente";
  };

  const attendanceColumns = [
    {
      key: "employeeName",
      label: "Empleado",
      render: (val, row) =>
        buscarColaborador(employees, row.employeeid)?.name || val || "Desconocido",
    },
    { key: "date", label: "Fecha", render: (val) => formatDate(val) },
    { key: "checkIn", label: "Entrada", render: (val) => formatTime(val) },
    { key: "checkOut", label: "Salida", render: (val) => formatTime(val) },
    {
      key: "hoursWorked",
      label: "Horas",
      render: (val) => (val != null ? `${Number(val).toFixed(1)}h` : "-"),
    },
    {
      key: "status",
      label: "Estado",
      render: (_, row) => <StatusBadge status={getStatus(row)} />,
    },
  ];

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaUsers}
          title="Seleccioná un edificio para ver la asistencia"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Encabezado */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaUserClock style={{ marginRight: 10, color: colors.primary }} />
          Asistencia de Empleados
        </h1>
      </div>

      {/* Tarjetas de resumen */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <FaUsers style={{ fontSize: 24, color: colors.primary }} />
          <div>
            <p style={styles.statValue}>{todayStats.present}</p>
            <p style={styles.statLabel}>Presentes hoy</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <FaClock style={{ fontSize: 24, color: colors.primary }} />
          <div>
            <p style={styles.statValue}>{todayStats.avgHours}h</p>
            <p style={styles.statLabel}>Promedio hs/semana</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <FaUsers style={{ fontSize: 24, color: colors.primary }} />
          <div>
            <p style={styles.statValue}>{employees.length}</p>
            <p style={styles.statLabel}>Total empleados</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersHeader}>
          <FaFilter style={{ marginRight: 8, color: colors.primary }} />
          <span>Filtros</span>
        </div>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Empleado</label>
            <select
              style={styles.select}
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">Todos</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.employeeid}>
                  {emp.nombre} {emp.apellido}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Desde</label>
            <input
              type="date"
              style={styles.input}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Hasta</label>
            <input
              type="date"
              style={styles.input}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <LoadingState variant="block" message="Cargando registros…" />
      ) : (
        <DataTable
          columns={attendanceColumns}
          data={attendance}
          emptyIcon={FaSearch}
          emptyTitle="No se encontraron registros de asistencia"
          emptyDescription="Ajustá los filtros o esperá a que se registren asistencias"
        />
      )}
    </div>
  );
};

const styles = {
  // Contenedor principal de la página
  container: {
    ...pageContainer,
  },
  header: {
    marginBottom: spacing[6],
  },
  title: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    display: "flex",
    alignItems: "center",
    margin: 0,
  },
  // Fila de tarjetas estadísticas
  statsRow: {
    display: "flex",
    gap: spacing[4],
    marginBottom: spacing[6],
    flexWrap: "wrap",
  },
  statCard: {
    ...cardBase,
    flex: 1,
    minWidth: 180,
    display: "flex",
    alignItems: "center",
    gap: spacing[4],
  },
  statValue: {
    fontSize: typography.sizes["2xl"],
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  statLabel: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  // Tarjeta de filtros
  filtersCard: {
    ...cardBase,
    marginBottom: spacing[6],
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: spacing[4],
    fontSize: 15,
    color: colors.black,
  },
  filtersRow: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 180,
  },
  filterLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  // Select de filtro
  select: {
    padding: "8px 12px",
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.md,
    fontFamily: typography.fontFamily,
    outline: "none",
    background: colors.white,
  },
  // Input de fecha
  input: {
    padding: "8px 12px",
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.md,
    fontFamily: typography.fontFamily,
    outline: "none",
  },
};

export default AsistenciaEmpleados;
